import React, { useEffect, useState } from 'react';
import { useT } from './i18n/I18nContext';

/**
 * Small bottom-of-screen footer showing the people who picked / pressed /
 * packed an order. Renders as Polaris-style chips for nice readability.
 *
 * Props:
 *   orderId      — numeric Shopify order id (or full GID; we strip)
 *   storeKey     — soccerpost / surf / rush / citysports
 *   fetchFn      — auth-aware fetch (so the call carries the right session)
 *   roles        — array of which sections to render. Default: ['pickers'].
 *                  Pass ['pickers','pressers'] in the Packing/QC Portal.
 *   placement    — 'inline' (renders inline at the JSX position — default,
 *                  used to render directly under the Working-at banner) or
 *                  'fixed' (sticky to viewport bottom — legacy).
 */
export default function PortalParticipantsFooter({
    orderId, storeKey = 'soccerpost', fetchFn,
    roles = ['pickers'],
    placement = 'inline',
}) {
    const t = useT();
    const [data, setData] = useState({ pickers: [], pressers: [], packers: [] });
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!orderId || !fetchFn) return;
        const numId = String(orderId).split('/').pop();
        let cancelled = false;
        fetchFn(`/api/orders/${numId}/portal-participants`, undefined, storeKey)
            .then(r => r.ok ? r.json() : null)
            .then(json => {
                if (cancelled || !json) return;
                setData({
                    pickers:  Array.isArray(json.pickers)  ? json.pickers  : [],
                    pressers: Array.isArray(json.pressers) ? json.pressers : [],
                    packers:  Array.isArray(json.packers)  ? json.packers  : [],
                });
                setLoaded(true);
            })
            .catch(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [orderId, storeKey, fetchFn]);

    const sections = [];
    if (roles.includes('pickers')  && data.pickers.length)  sections.push({ label: t('Picker(s)'),  names: data.pickers,  tone: 'pickers'  });
    if (roles.includes('pressers') && data.pressers.length) sections.push({ label: t('Presser(s)'), names: data.pressers, tone: 'pressers' });
    if (roles.includes('packers')  && data.packers.length)  sections.push({ label: t('Packer(s)'),  names: data.packers,  tone: 'packers'  });

    if (!loaded || sections.length === 0) return null;

    return (
        <div className={`portal-participants-footer portal-participants-footer--${placement}`}>
            <div className="portal-participants-footer__inner">
                {sections.map((sec, i) => (
                    <div key={sec.label} className="portal-participants-footer__group">
                        <span className="portal-participants-footer__label">{sec.label}:</span>
                        <span className="portal-participants-footer__chips">
                            {sec.names.map(name => (
                                <span
                                    key={name}
                                    className={`portal-participants-footer__chip portal-participants-footer__chip--${sec.tone}`}
                                >
                                    {name}
                                </span>
                            ))}
                        </span>
                        {i < sections.length - 1 && (
                            <span className="portal-participants-footer__sep" aria-hidden="true">•</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
